import { supabase } from './supabase';

// Member Communication Hub Service
export const communicationService = {
  // Announcements
  async getAnnouncements(gymId, filters = {}) {
    try {
      let query = supabase
        ?.from('announcements')
        ?.select(`
          *,
          created_by_profile:user_profiles!announcements_created_by_fkey(id, full_name, role)
        `)
        ?.eq('gym_id', gymId)
        ?.eq('is_active', true);

      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      const { data, error } = await query?.order('published_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createAnnouncement(gymId, announcementData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('announcements')
        ?.insert({
          gym_id: gymId,
          created_by: user?.id,
          ...announcementData
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async markAnnouncementAsRead(announcementId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('announcement_reads')
        ?.insert({
          announcement_id: announcementId,
          user_id: user?.id
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getUnreadAnnouncementsCount(gymId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: announcements, error: announcementsError } = await supabase
        ?.from('announcements')
        ?.select('id')
        ?.eq('gym_id', gymId)
        ?.eq('is_active', true);

      if (announcementsError) throw announcementsError;

      const { data: reads, error: readsError } = await supabase
        ?.from('announcement_reads')
        ?.select('announcement_id')
        ?.eq('user_id', user?.id);

      if (readsError) throw readsError;

      const readIds = new Set(reads?.map(r => r?.announcement_id));
      const unreadCount = announcements?.filter(a => !readIds?.has(a?.id))?.length || 0;

      return { data: unreadCount, error: null };
    } catch (error) {
      return { data: 0, error };
    }
  },

  // Community Posts
  async getCommunityPosts(gymId, filters = {}) {
    try {
      let query = supabase
        ?.from('community_posts')
        ?.select(`
          *,
          author:user_profiles!community_posts_author_id_fkey(id, full_name, role, avatar_url)
        `)
        ?.eq('gym_id', gymId)
        ?.eq('is_approved', true);

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      const { data, error } = await query?.order('is_pinned', { ascending: false })?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createCommunityPost(gymId, postData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('community_posts')
        ?.insert({
          gym_id: gymId,
          author_id: user?.id,
          ...postData
        })
        ?.select(`
          *,
          author:user_profiles!community_posts_author_id_fkey(id, full_name, role, avatar_url)
        `)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async updateCommunityPost(postId, updates) {
    try {
      const { data, error } = await supabase
        ?.from('community_posts')
        ?.update(updates)
        ?.eq('id', postId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteCommunityPost(postId) {
    try {
      const { error } = await supabase
        ?.from('community_posts')
        ?.delete()
        ?.eq('id', postId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Post Comments
  async getPostComments(postId) {
    try {
      const { data, error } = await supabase
        ?.from('post_comments')
        ?.select(`
          *,
          author:user_profiles!post_comments_author_id_fkey(id, full_name, role, avatar_url)
        `)
        ?.eq('post_id', postId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async createComment(postId, content) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('post_comments')
        ?.insert({
          post_id: postId,
          author_id: user?.id,
          content
        })
        ?.select(`
          *,
          author:user_profiles!post_comments_author_id_fkey(id, full_name, role, avatar_url)
        `)
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async deleteComment(commentId) {
    try {
      const { error } = await supabase
        ?.from('post_comments')
        ?.delete()
        ?.eq('id', commentId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Post Likes
  async togglePostLike(postId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Check if already liked
      const { data: existingLike } = await supabase
        ?.from('post_likes')
        ?.select('id')
        ?.eq('post_id', postId)
        ?.eq('user_id', user?.id)
        ?.single();

      if (existingLike) {
        // Unlike
        const { error } = await supabase
          ?.from('post_likes')
          ?.delete()
          ?.eq('id', existingLike?.id);

        if (error) throw error;
        return { data: { liked: false }, error: null };
      } else {
        // Like
        const { data, error } = await supabase
          ?.from('post_likes')
          ?.insert({
            post_id: postId,
            user_id: user?.id
          })
          ?.select()
          ?.single();

        if (error) throw error;
        return { data: { liked: true }, error: null };
      }
    } catch (error) {
      return { data: null, error };
    }
  },

  async checkIfUserLikedPost(postId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return { data: false, error: null };

      const { data, error } = await supabase
        ?.from('post_likes')
        ?.select('id')
        ?.eq('post_id', postId)
        ?.eq('user_id', user?.id)
        ?.single();

      return { data: !!data, error: null };
    } catch (error) {
      return { data: false, error: null };
    }
  },

  // AI Help Interactions
  async saveAIHelpInteraction(gymId, pageContext, question, response) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        ?.from('ai_help_interactions')
        ?.insert({
          user_id: user?.id,
          gym_id: gymId,
          page_context: pageContext,
          question,
          response
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async submitAIHelpFeedback(interactionId, wasHelpful, feedback) {
    try {
      const { data, error } = await supabase
        ?.from('ai_help_interactions')
        ?.update({
          was_helpful: wasHelpful,
          feedback
        })
        ?.eq('id', interactionId)
        ?.select()
        ?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Real-time subscriptions
  subscribeToCommunityPosts(gymId, callback) {
    const subscription = supabase
      ?.channel(`community_posts:${gymId}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'community_posts',
          filter: `gym_id=eq.${gymId}`
        },
        callback
      )
      ?.subscribe();

    return subscription;
  },

  subscribeToAnnouncements(gymId, callback) {
    const subscription = supabase
      ?.channel(`announcements:${gymId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'announcements',
          filter: `gym_id=eq.${gymId}`
        },
        callback
      )
      ?.subscribe();

    return subscription;
  }
};